import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

export default class SmoothScroller {
  constructor(element) {
    gsap.registerPlugin(ScrollSmoother, ScrollTrigger);
    this.options = { hasSkew: false, hasPinItems: false };
    this.element = element;
    this.init();
    this.setOptions();
    this.initAnchors();
  }

  init() {
    this.scroller = ScrollSmoother.create({
      smooth: 2,
      effects: true,
      smoothTouch: 0.1,
      onUpdate: this.onUpdateScroll.bind(this),
      onStop: this.onStopScroll.bind(this),
    });
  }

  onUpdateScroll(self) {
    if (this.options.hasSkew) this.updateSkew(self);
  }

  onStopScroll(self) {
    if (this.options.hasSkew) this.stopSkew(self);
  }
  //
  initSkew() {
    this.skewSetter = gsap.quickTo('img', 'skewY');
  }
  updateSkew(self) {
    const velocity = self.getVelocity();
    const force = 5;
    let skew = gsap.utils.mapRange(-1000, 1000, -force, force, velocity);
    skew = gsap.utils.clamp(-force, force, skew);
    this.skewSetter(skew);
  }
  stopSkew() {
    this.skewSetter(0);
  }
  initPins() {
    const pinnedItems = this.element.querySelectorAll('.js-pinned');
    for (let i = 0; i < pinnedItems.length; i++) {
      const pinnedItem = pinnedItems[i];
      ScrollTrigger.create({
        pin: pinnedItem,
        trigger: pinnedItem.parentElement,
        start: 'center center',
        end: '80% center',
      });
    }
  }
  initHoriz() {
    const sectionHoriz = this.element.querySelector('.js-horiz');
    const panels = sectionHoriz.querySelectorAll('.js-panel');
    const nbPanels = panels.length - 1;
    const buffer = 200;
    gsap.to(panels, {
      xPercent: -100 * nbPanels,
      ease: 'none',
      scrollTrigger: {
        pin: true,
        trigger: sectionHoriz,
        scrub: 1,
        snap: 1 / nbPanels,
        end: () => '+=' + (sectionHoriz.offsetWidth + buffer),
      },
    });
  }
  setOptions() {
    if ('skew' in this.element.dataset) {
      this.options.hasSkew = true;
      this.initSkew();
    }
    if ('pinItems' in this.element.dataset) {
      this.options.hasPinItems = true;
      this.initPins();
    }
    if (this.element.querySelector('.js-horiz')) {
      this.initHoriz();
    }
  }
  initAnchors() {
    // Le navigateur remet parfois la page à sa position précédente tout seul
    history.scrollRestoration = 'manual';

    this.guardNativeScroll();

    // 1. Clic sur un lien d'ancre qui pointe vers la page courante
    document.querySelectorAll('a[href*="#"]').forEach((anchor) => {
      if (!this.isSamePage(anchor)) return; // autre page : navigation normale

      anchor.addEventListener('click', (event) => {
        const target = this.getTarget(anchor.hash);
        if (!target) return;

        event.preventDefault();
        history.pushState(null, '', anchor.hash); // met l'URL à jour sans saut natif
        this.scroller.scrollTo(target, true, 'top top'); // true = animé
      });
    });

    // 2. Arrivée sur la page avec un #hash dans l'URL (ex.: venant de l'accueil)
    if (document.readyState === 'complete') {
      this.scrollToHash();
    } else {
      window.addEventListener('load', () => this.scrollToHash());
    }

    // 3. Boutons précédent/suivant du navigateur entre deux ancres
    window.addEventListener('hashchange', () => this.scrollToHash(true));
  }

  // Le lien pointe-t-il vers la page actuelle ? ("aboutme", "aboutme.html", "/aboutme/"...)
  isSamePage(anchor) {
    const clean = (path) =>
      path
        .replace(/index\.html$/, '')
        .replace(/\.html$/, '')
        .replace(/\/$/, '') || '/';
    return clean(anchor.pathname) === clean(location.pathname);
  }

  getTarget(hash) {
    if (!hash || hash.length < 2) return null;
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  }

  scrollToHash(smooth = false) {
    const target = this.getTarget(location.hash);
    if (!target) return;

    // Remet à zéro tout décalage natif causé par le saut de l'ancre
    this.resetNativeScroll();
    // La hauteur de la page est maintenant connue (images, polices chargées)
    ScrollTrigger.refresh();
    requestAnimationFrame(() => {
      this.scroller.scrollTo(target, smooth, 'top top');
    });
  }

  // ScrollSmoother met le contenu dans un #smooth-wrapper fixe en overflow: hidden.
  // Quand le navigateur saute à une ancre (ou donne le focus à un élément), il fait
  // défiler CE wrapper en interne : le contenu se retrouve décalé, la page ne
  // peut plus remonter et le défilement semble "brisé".
  resetNativeScroll() {
    const wrapper = this.scroller.wrapper();
    wrapper.scrollTop = 0;
    wrapper.scrollLeft = 0;
  }

  // Garde-fou : si quoi que ce soit fait défiler le wrapper, on le remet à 0
  guardNativeScroll() {
    const wrapper = this.scroller.wrapper();
    wrapper.addEventListener('scroll', () => {
      if (wrapper.scrollTop !== 0 || wrapper.scrollLeft !== 0) {
        this.resetNativeScroll();
      }
    });
  }
}
